import React, { useEffect, useState, forwardRef, useCallback, useMemo } from 'react';
import i18n from '../../../i18n';

const Period = forwardRef(({
  fieldName,
  wrapperClass = '',
  label,
  hideLabel = false,
  allowNull = true,
  value = '',
  errors = [],
  helpText = '',
  parent = '',
  autofocus = false,
  disabled = false,
  format = 'PYMD',
  width = '',
  style = {},
  onChange = () => {},
  pk = null
}, ref) => {
  // Parse format to determine which fields to show
  // Format string like 'PYMD' or 'PYMWD' indicates which fields to display
  const formatConfig = useMemo(() => {
    const formatUpper = (format || 'PYMD').toUpperCase();
    const hasYears = formatUpper.includes('Y');
    const hasMonths = formatUpper.includes('M');
    const hasWeeks = formatUpper.includes('W');
    const hasDays = formatUpper.includes('D');
    
    return {
      hasYears,
      hasMonths,
      hasWeeks,
      hasDays
    };
  }, [format]);

  // Parse ISO-8601 period string (e.g., "P1Y6M", "P2W", "P10D", "P1Y2M3W4D")
  const parseValue = useCallback((val) => {
    if (!val || typeof val !== 'string' || val.trim() === '') {
      return { years: 0, months: 0, weeks: 0, days: 0 };
    }

    const trimmed = val.trim().toUpperCase();
    
    // Must start with 'P'
    if (!trimmed.startsWith('P')) {
      return { years: 0, months: 0, weeks: 0, days: 0 };
    }

    // Remove 'P' prefix
    const periodStr = trimmed.substring(1);
    
    // Parse ISO-8601 period format: P(nY)?(nM)?(nW)?(nD)?
    const yearsMatch = periodStr.match(/(\d+)Y/);
    const monthsMatch = periodStr.match(/(\d+)M/);
    const weeksMatch = periodStr.match(/(\d+)W/);
    const daysMatch = periodStr.match(/(\d+)D/);

    const years = yearsMatch ? parseInt(yearsMatch[1]) || 0 : 0;
    const months = monthsMatch ? parseInt(monthsMatch[1]) || 0 : 0;
    const weeks = weeksMatch ? parseInt(weeksMatch[1]) || 0 : 0;
    const days = daysMatch ? parseInt(daysMatch[1]) || 0 : 0;

    return { years, months, weeks, days };
  }, []);

  const [period, setPeriod] = useState(() => parseValue(value));

  // Parse initial value
  useEffect(() => {
    setPeriod(parseValue(value));
  }, [value, parseValue]);

  // Format period to ISO-8601 string (e.g., "P1Y6M", "P2W", "P10D")
  const formatPeriod = useCallback((per) => {
    const { hasYears, hasMonths, hasWeeks, hasDays } = formatConfig;
    
    // Get values, defaulting to 0 if field is not shown
    const years = hasYears ? (per.years || 0) : 0;
    const months = hasMonths ? (per.months || 0) : 0;
    const weeks = hasWeeks ? (per.weeks || 0) : 0;
    const days = hasDays ? (per.days || 0) : 0;

    // Build ISO-8601 period string
    let result = 'P';
    if (years > 0) result += `${years}Y`;
    if (months > 0) result += `${months}M`;
    if (weeks > 0) result += `${weeks}W`;
    if (days > 0) result += `${days}D`;

    // Return empty string if all values are 0 (or null if allowNull)
    if (result === 'P') {
      return allowNull ? '' : 'P0D';
    }

    return result;
  }, [formatConfig, allowNull]);

  // Handle individual field changes
  const handleFieldChange = useCallback((field, newValue) => {
    const numValue = Math.max(0, parseInt(newValue) || 0);
    const newPeriod = { ...period, [field]: numValue };
    
    setPeriod(newPeriod);
    
    // Call onChange with formatted ISO-8601 value
    const formattedValue = formatPeriod(newPeriod);
    onChange({
      'name': fieldName,
      'value': formattedValue,
      'label': formattedValue
    });
  }, [period, fieldName, onChange, formatPeriod]);

  // Handle input change
  const handleInputChange = useCallback((field, event) => {
    const newValue = event.target.value;
    handleFieldChange(field, newValue);
  }, [handleFieldChange]);

  // Handle input blur for validation
  const handleInputBlur = useCallback((field, event) => {
    const newValue = event.target.value;
    const numValue = Math.max(0, parseInt(newValue) || 0);
    handleFieldChange(field, numValue);
  }, [handleFieldChange]);

  const { hasYears, hasMonths, hasWeeks, hasDays } = formatConfig;
  
  // Calculate column width based on number of visible fields
  const visibleFieldsCount = [hasYears, hasMonths, hasWeeks, hasDays].filter(Boolean).length;
  const colClass = visibleFieldsCount > 0 ? `col-${Math.floor(12 / visibleFieldsCount)}` : 'col-3';

  return (
      <div className="row g-2">
        {hasYears && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Years')}</label>
            <input
              ref={fieldName === 'years' ? ref : null}
              id={pk ? `${pk}_${fieldName}_years` : `${fieldName}_years`}
              name={`${fieldName}_years`}
              type="number"
              min="0"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={period.years}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('years', e)}
              onBlur={(e) => handleInputBlur('years', e)}
              placeholder="0"
            />
          </div>
        )}
        {hasMonths && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Months')}</label>
            <input
              id={pk ? `${pk}_${fieldName}_months` : `${fieldName}_months`}
              name={`${fieldName}_months`}
              type="number"
              min="0"
              max="11"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={period.months}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('months', e)}
              onBlur={(e) => handleInputBlur('months', e)}
              placeholder="0"
            />
          </div>
        )}
        {hasWeeks && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Weeks')}</label>
            <input
              id={pk ? `${pk}_${fieldName}_weeks` : `${fieldName}_weeks`}
              name={`${fieldName}_weeks`}
              type="number"
              min="0"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={period.weeks}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('weeks', e)}
              onBlur={(e) => handleInputBlur('weeks', e)}
              placeholder="0"
            />
          </div>
        )}
        {hasDays && (
          <div className={colClass}>
            <label className="mb-0 form-label small text-muted">{i18n.t('Days')}</label>
            <input
              id={pk ? `${pk}_${fieldName}_days` : `${fieldName}_days`}
              name={`${fieldName}_days`}
              type="number"
              min="0"
              className={`form-control form-control-sm ${errors.length > 0 ? 'is-invalid' : ''}`}
              value={period.days}
              disabled={disabled}
              data-char_select_parent={parent}
              onChange={(e) => handleInputChange('days', e)}
              onBlur={(e) => handleInputBlur('days', e)}
              placeholder="0"
            />
          </div>
        )}
      </div>
  );
});

export default Period;
